import { fromIqd, fromMillidinars, toIqdDecimalString, type UpdateBillInput } from '@gc/shared';

/**
 * What the bill editor sends — only what the cashier actually changed.
 *
 * A settled bill's discount is rarely whole IQD: the checkout folds its
 * cash-step rounding into the discount split, and on 2026-09-23 4,848 of the
 * 10,461 paid bills carried a discount like 250.074. The editor used to prefill
 * that into a `step="1"` box, which the browser refuses to submit, and then send
 * `Math.round(discount)` on every save — so fixing a visitor's name meant first
 * changing the discount, and the rounded discount rewrote the bill's total away
 * from what was paid. The field now shows the exact figure, and the discount
 * travels only when it was edited.
 */

/** The exact discount as the input shows it: `250074` md -> `"250.074"`, `250000` -> `"250"`. */
export function discountFieldValue(discountMd: string): string {
  return toIqdDecimalString(fromMillidinars(discountMd)).replace(/\.?0+$/, '') || '0';
}

export type DiscountEdit =
  | { kind: 'unchanged' }
  | { kind: 'changed'; discountIqd: number }
  /** Not a whole, non-negative IQD amount — the API takes nothing else. */
  | { kind: 'invalid' };

/**
 * Reads the discount box against the bill's current discount. Equal in
 * millidinars is unchanged however it was typed ("250" and "250.0" alike); an
 * emptied box is a discount of zero, as it always was.
 */
export function readDiscountEdit(discountMd: string, entered: string): DiscountEdit {
  const trimmed = entered.trim();
  const value = trimmed === '' ? 0 : Number(trimmed);
  if (!Number.isFinite(value) || value < 0) return { kind: 'invalid' };
  if (sameAmount(value, discountMd)) return { kind: 'unchanged' };
  if (!Number.isSafeInteger(value)) return { kind: 'invalid' };
  return { kind: 'changed', discountIqd: value };
}

function sameAmount(iqd: number, discountMd: string): boolean {
  try {
    return fromIqd(iqd) === fromMillidinars(discountMd);
  } catch {
    // Past the float-safe range: no bill's discount, so not the same.
    return false;
  }
}

export interface BillEditDraft {
  discountMd: string;
  discountEntered: string;
  /** The bill's current walk-in name; null on a registered customer's bill. */
  visitorName: string | null;
  visitorEntered: string;
  /** Walk-ins only: a registered account is a customer-records action. */
  canRenameVisitor: boolean;
  reason: string;
}

export type BillEditPlan =
  | { kind: 'ready'; payload: UpdateBillInput }
  | { kind: 'nothingChanged' }
  | { kind: 'invalidDiscount' };

export function planBillEdit(draft: BillEditDraft): BillEditPlan {
  const discount = readDiscountEdit(draft.discountMd, draft.discountEntered);
  if (discount.kind === 'invalid') return { kind: 'invalidDiscount' };

  const visitorName = draft.visitorEntered.trim() || null;
  const renamed = draft.canRenameVisitor && visitorName !== (draft.visitorName?.trim() || null);

  if (discount.kind === 'unchanged' && !renamed) return { kind: 'nothingChanged' };

  return {
    kind: 'ready',
    payload: {
      reason: draft.reason,
      ...(discount.kind === 'changed' ? { discountIqd: discount.discountIqd } : {}),
      ...(renamed ? { visitorName } : {}),
    },
  };
}
